import { Clock, CheckCircle, Info, ShieldCheck, GraduationCap, LogOut } from 'lucide-react';

export default function SpecialAdmissionDashboard({ user, onLogout }) {
    return (
        <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6 font-sans">
            <div className="max-w-xl w-full bg-white rounded-3xl shadow-2xl border border-slate-200 overflow-hidden animate-in zoom-in-95 duration-500">
                {/* Header */}
                <div className="bg-gradient-to-br from-indigo-600 to-blue-800 p-10 text-white text-center relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16 blur-2xl"></div>
                    <div className="absolute bottom-0 left-0 w-24 h-24 bg-indigo-400/10 rounded-full -ml-12 -mb-12 blur-xl"></div>
                    
                    <div className="relative z-10">
                        <div className="w-20 h-20 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-6 backdrop-blur-md shadow-inner border border-white/30">
                            <ShieldCheck size={40} className="text-white" />
                        </div>
                        <h1 className="text-2xl font-bold mb-2 tracking-tight">Admisión Especial en Trámite</h1>
                        <p className="text-indigo-100/80 text-sm font-medium">
                            Tu registro ha sido recibido correctamente y se encuentra en revisión.
                        </p>
                    </div>
                </div>

                {/* Content */}
                <div className="p-8 space-y-8">
                    {/* Welcome Section */}
                    <div className="text-center">
                        <h2 className="text-slate-800 font-bold text-lg mb-1">¡Bienvenido a la EBID!</h2>
                        <p className="text-slate-500 text-sm">
                            Hola <span className="text-indigo-600 font-bold">{user?.userName || 'Estudiante'}</span>, tu proceso de admisión especial está siguiendo los canales regulares.
                        </p>
                    </div>

                    {/* Timeline/Steps */}
                    <div className="space-y-4">
                        <div className="flex items-start gap-4 p-4 bg-emerald-50 rounded-2xl border border-emerald-100">
                            <div className="w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center shrink-0 shadow-sm">
                                <CheckCircle size={18} className="text-white" />
                            </div>
                            <div>
                                <h3 className="text-sm font-bold text-emerald-900">Paso 1: Registro de Datos</h3>
                                <p className="text-xs text-emerald-700/80">Tus datos personales y documentos han sido cargados al sistema.</p>
                            </div>
                        </div>

                        <div className="flex items-start gap-4 p-4 bg-blue-50 rounded-2xl border border-blue-100 animate-pulse">
                            <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center shrink-0 shadow-sm shadow-blue-200">
                                <Clock size={18} className="text-white" />
                            </div>
                            <div>
                                <h3 className="text-sm font-bold text-blue-900">Paso 2: Validación de Kardex</h3>
                                <p className="text-xs text-blue-700/80">El Departamento de Kardex está verificando tu información para proceder con tu matriculación oficial.</p>
                            </div>
                        </div>

                        <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-2xl border border-slate-100 opacity-60">
                            <div className="w-8 h-8 bg-slate-300 rounded-full flex items-center justify-center shrink-0">
                                <GraduationCap size={18} className="text-white" />
                            </div>
                            <div>
                                <h3 className="text-sm font-bold text-slate-500">Paso 3: Estudiante Matriculado</h3>
                                <p className="text-xs text-slate-400">Una vez aprobado, tendrás acceso completo a tu historial, materias y notas.</p>
                            </div>
                        </div>
                    </div>

                    {/* Notice Info */}
                    <div className="bg-amber-50 border border-amber-100 p-5 rounded-2xl flex gap-4">
                        <Info className="text-amber-500 shrink-0" size={20} />
                        <p className="text-xs text-amber-800 leading-relaxed">
                            <span className="font-bold block mb-1">Nota Importante:</span>
                            Mientras tu solicitud esté siendo procesada, no podrás inscribirte a materias ni visualizar registros académicos. Este proceso suele tardar entre 24 a 48 horas hábiles.
                        </p>
                    </div>

                    {/* Actions/Contact */}
                    <div className="pt-6 border-t border-slate-100 flex flex-col items-center gap-4">
                        <p className="text-xs text-slate-400 font-medium">¿Tienes alguna duda sobre tu proceso?</p>
                        <div className="flex gap-3 w-full">
                            <div className="flex-1 py-3 px-4 bg-slate-100 rounded-xl text-center">
                                <span className="block text-[9px] font-bold text-slate-400 uppercase">Estado Actual</span>
                                <span className="text-xs font-bold text-slate-700 uppercase tracking-tighter">Pendiente de Aprobación</span>
                            </div>
                            <div className="flex-1 py-3 px-4 bg-indigo-50 rounded-xl text-center border border-indigo-100">
                                <span className="block text-[9px] font-bold text-indigo-400 uppercase">Modalidad</span>
                                <span className="text-xs font-bold text-indigo-700 uppercase tracking-tighter">Admisión Especial</span>
                            </div>
                        </div>

                        <button 
                            onClick={onLogout}
                            className="mt-4 flex items-center gap-2 px-8 py-3 bg-slate-800 text-white rounded-2xl hover:bg-slate-900 transition-all shadow-lg text-sm font-bold"
                        >
                            <LogOut size={18} />
                            Cerrar Sesión
                        </button>
                    </div>
                </div>

                {/* Footer */}
                <div className="bg-slate-50 py-5 px-8 text-center border-t border-slate-100">
                    <div className="flex items-center justify-center gap-2 mb-1">
                        <ShieldCheck size={14} className="text-slate-400" />
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.2em]">
                            EBID • Sistema de Seguridad Académica
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}
